import { WORLD_HEIGHT, WORLD_WIDTH } from '../config.ts';
import type { Collider } from '../simulation/Collision.ts';

/** What has to arrive for the level to be solved. */
export type GoalType = 'fluid' | 'ball';

/** A level is pure data: no behaviour, no rendering, no Pixi. */
export interface LevelDefinition {
  id: string;
  name: string;
  /** One line shown on the title card. */
  hint: string;
  magnetsAvailable: number;
  fluid: {
    /** Particles placed at the source before the run starts. */
    amount: number;
    source: { x: number; y: number };
    /** Radius of the reservoir mouth. */
    radius: number;
  };
  /** Omitted on levels whose objective is the fluid itself. */
  ball?: { x: number; y: number; radius: number };
  goal: {
    x: number;
    y: number;
    radius: number;
    type: GoalType;
    /**
     * Fluid goals only: the fraction of the level's fluid that has to be inside the goal
     * at once. Well under 1, because some of a blob always lags behind.
     */
    fraction?: number;
  };
  walls: Collider[];
}

/** Thickness of the invisible box that keeps everything inside the playfield. */
const BOUNDS_THICKNESS = 200;

/** The four walls of the world box, positioned just outside the visible area. */
export function worldBounds(): Collider[] {
  const halfThickness = BOUNDS_THICKNESS / 2;
  return [
    {
      type: 'rect',
      x: WORLD_WIDTH / 2,
      y: -halfThickness,
      w: WORLD_WIDTH + BOUNDS_THICKNESS,
      h: BOUNDS_THICKNESS,
    },
    {
      type: 'rect',
      x: WORLD_WIDTH / 2,
      y: WORLD_HEIGHT + halfThickness,
      w: WORLD_WIDTH + BOUNDS_THICKNESS,
      h: BOUNDS_THICKNESS,
    },
    {
      type: 'rect',
      x: -halfThickness,
      y: WORLD_HEIGHT / 2,
      w: BOUNDS_THICKNESS,
      h: WORLD_HEIGHT + BOUNDS_THICKNESS,
    },
    {
      type: 'rect',
      x: WORLD_WIDTH + halfThickness,
      y: WORLD_HEIGHT / 2,
      w: BOUNDS_THICKNESS,
      h: WORLD_HEIGHT + BOUNDS_THICKNESS,
    },
  ];
}
